import React, { useState } from "react";
import { ShieldCheck, Mail } from "lucide-react";
import api from "@/lib/api";
import { Field, Label, Check, Honeypot } from "./FormBits";
import { errMsg } from "./marketingConstants";

const EMPTY = {
  contact_name: "", brand_name: "", email: "", phone: "", location: "", website_or_social: "",
  inquiry_type: "marketing", product_category: "", goals: "", message: "",
  budget_range: "", desired_timing: "", consent: false, hp_field: "",
};

export function BusinessInquiryForm({ initialType = "marketing" }) {
  const [form, setForm] = useState({ ...EMPTY, inquiry_type: initialType });
  const [status, setStatus] = useState({ kind: "idle", msg: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.contact_name || !form.brand_name || !form.email || !form.goals) {
      setStatus({ kind: "err", msg: "Please fill in name, brand, email, and goals." });
      return;
    }
    if (!form.consent) {
      setStatus({ kind: "err", msg: "Please confirm we may contact you about this inquiry." });
      return;
    }
    setStatus({ kind: "loading", msg: "" });
    try {
      await api.post("/marketing/inquiries", { ...form, audience_type: "business" });
      setStatus({ kind: "ok", msg: "Inquiry received. We'll be in touch within 2 business days." });
      setForm(EMPTY);
    } catch (err) {
      setStatus({ kind: "err", msg: errMsg(err, "We couldn't submit your inquiry. Please try again.") });
    }
  }

  return (
    <form onSubmit={submit} className="glass-panel p-6 space-y-4" data-testid="marketing-inquiry-form">
      <Honeypot value={form.hp_field} onChange={(v) => set("hp_field", v)} />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Contact name *" value={form.contact_name} onChange={(v) => set("contact_name", v)} testid="field-contact-name" />
        <Field label="Brand name *" value={form.brand_name} onChange={(v) => set("brand_name", v)} testid="field-brand-name" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="field-email" />
        <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="field-phone" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Website or social profile" value={form.website_or_social} onChange={(v) => set("website_or_social", v)} testid="field-social" />
        <Field label="Location" value={form.location} onChange={(v) => set("location", v)} testid="field-location" placeholder="City, State" />
      </div>
      <div>
        <Label>Inquiry type *</Label>
        <div className="flex gap-2 mt-1">
          {["marketing", "partnership"].map((t) => (
            <label key={t} className={`flex-1 cursor-pointer border rounded px-3 py-2 text-xs font-bebas uppercase tracking-wider text-center transition-colors ${
              form.inquiry_type === t ? "border-lifted-green bg-lifted-green/10 text-lifted-green" : "border-white/20 text-white/70"
            }`} data-testid={`inquiry-type-${t}`}>
              <input type="radio" value={t} checked={form.inquiry_type === t} onChange={() => set("inquiry_type", t)} className="hidden" />
              {t}
            </label>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Product category" value={form.product_category} onChange={(v) => set("product_category", v)} testid="field-category" placeholder="e.g. apparel, CBD, accessories" />
        <Field label="Budget range" value={form.budget_range} onChange={(v) => set("budget_range", v)} testid="field-budget" placeholder="e.g. $5k–$20k" />
      </div>
      <Field label="Desired timing" value={form.desired_timing} onChange={(v) => set("desired_timing", v)} testid="field-timing" placeholder="e.g. Launch in 60 days" />
      <div>
        <Label>Goals *</Label>
        <textarea className="input-lifted w-full min-h-[110px]" value={form.goals}
          onChange={(e) => set("goals", e.target.value)} placeholder="What are you trying to achieve?" data-testid="field-goals" />
      </div>
      <Check checked={form.consent} onChange={(v) => set("consent", v)} testid="field-consent">
        I agree that Lifted may contact me about this inquiry. Services are requests subject to approval and availability.
      </Check>
      <button type="submit" disabled={status.kind === "loading"} className="btn-lifted w-full disabled:opacity-50" data-testid="marketing-submit">
        {status.kind === "loading" ? "Sending…" : "Send inquiry"}
      </button>
      {status.kind === "ok" && (
        <div className="flex items-center gap-2 text-sm text-lifted-green" data-testid="marketing-success">
          <ShieldCheck className="h-4 w-4" /> {status.msg}
        </div>
      )}
      {status.kind === "err" && <div className="text-sm text-rose-300" data-testid="marketing-error">{status.msg}</div>}
      <p className="text-[11px] text-white/40 pt-1 flex items-center gap-1">
        <Mail className="h-3 w-3" /> Direct replies will come from the Lifted team.
      </p>
    </form>
  );
}
